import {
  useDomainOptions,
  type DomainOption,
} from '@components/FormDomainSelect';
import {
  useCanEditGovernanceDomain,
  useGovernanceAccess,
} from '@/hooks/useGovernanceAccess';

/**
 * Labels of the selected domains without an active Content Policy licence. Ids
 * the options do not cover yet are left out rather than printed raw: the option
 * list arrives asynchronously, and a report id means nothing to a customer.
 * Callers therefore render their warning off this list, not off the ids.
 */
export function getUnlicensedDomainLabels(
  selectedIds: number[],
  options: DomainOption[],
  licensedDomainIds: number[],
): string[] {
  const licensed = new Set(licensedDomainIds);
  const labels = new Map(options.map(({ id, label }) => [id, label]));

  return selectedIds
    .filter((id) => !licensed.has(id))
    .map((id) => labels.get(id))
    .filter((label): label is string => label !== undefined);
}

export function filterEditableGovernanceDomainOptions(
  options: DomainOption[],
  canEditDomain: (domainId: number) => boolean,
): DomainOption[] {
  return options.filter(({ id }) => canEditDomain(id));
}

/**
 * Domain choices for policy forms; privileged users retain admin search.
 *
 * `referencedIds` are the domains the policy already covers; the licence
 * bootstrap only holds a bounded window, so they are requested explicitly to
 * keep a scoped domain from dropping out of the picker.
 */
export function useEditableGovernanceDomainOptions(
  referencedIds: number[] = [],
): DomainOption[] | undefined {
  const governanceAccess = useGovernanceAccess();
  const canEditDomain = useCanEditGovernanceDomain();
  const availableDomainOptions = useDomainOptions(referencedIds);

  return governanceAccess.isPrivileged
    ? undefined
    : filterEditableGovernanceDomainOptions(
        availableDomainOptions,
        canEditDomain,
      );
}
